/**
 * BLRRD Color — ACES helpers beyond the static transforms.
 *
 *   ACEScct encode/decode  — log encoding for curve-panel slider feel.
 *                            Resolve / Baselight / Nuke colorists expect
 *                            curves to behave on a log axis; we evaluate
 *                            curves in ACEScct and convert back to ACEScg.
 *
 *   filmic                  — RRT-free tone-mapper (Narkowicz-ACES-fitted).
 *                             Use this when ODT collapses to an LDR display.
 *                             The full Academy RRT is not used — see
 *                             docs/COLORSPACE-DESIGN.md for the rationale.
 *
 *   sigmoid (Phase 1.H)     — closed-form modified generalized log-logistic
 *                             curve (darktable 4.4+ formulation). Middle-
 *                             ground tone-map between neutral and filmic:
 *                             tone-maps highlights without baking filmic's
 *                             midtone S-curve. Per-channel; opt-in via
 *                             `viewTransform({ tone: 'sigmoid' })`.
 *
 * Status: Phase 2.C skeleton. ACEScct transfer is wired (analytical, cheap);
 *   filmic is also wired since it's a single fitted polynomial. Sigmoid added
 *   in Phase 1.H per docs/superpowers/specs/2026-05-07-sigmoid-view-transform.md.
 */

/* ACEScct piecewise log curve (Academy S-2016-001). Linear toe ≤ 0.0078125,
 * log shoulder above. Designed so log-mid-grey lands at ~0.4135884 (matches
 * 18% middle grey under linear→log convention). */
const ACEScct_LIN_MIN = 0.0078125;
const ACEScct_LOG_BREAK = 10.5402377416545;            /* (log2(0.0078125 + 0.5) + 9.72) / 17.52 */
const ACEScct_LOG_OFFSET = 9.72;
const ACEScct_LOG_SCALE  = 17.52;

export function encodeACEScct(v) {
    if (v <= ACEScct_LIN_MIN) return ACEScct_LOG_BREAK * v + 0.0729055341958355;
    return (Math.log2(v) + ACEScct_LOG_OFFSET) / ACEScct_LOG_SCALE;
}
export function decodeACEScct(v) {
    if (v <= 0.155251141552511) return (v - 0.0729055341958355) / ACEScct_LOG_BREAK;
    return Math.pow(2, v * ACEScct_LOG_SCALE - ACEScct_LOG_OFFSET);
}

/**
 * Narkowicz ACES-fitted polynomial — single-shot tone-map for ACEScg input
 * targeting an LDR display. Per-channel; preserves saturation reasonably.
 *
 *   color' = (c · (a·c + b)) / (c · (d·c + e) + f)
 *
 * Tuned coefficients from "ACES Filmic Tone Mapping Curve" (Narkowicz, 2015).
 * NOT the full Academy RRT — that ships proprietary look-baking we don't want.
 *
 * @param {Float32Array} rgba   ACEScg-linear RGBA Float32
 * @param {Float32Array} [dst]
 * @returns {Float32Array}      [0..1] display-linear RGBA (still needs ODT encode)
 */
export function filmicACES(rgba, dst) {
    const a = 2.51, b = 0.03, c = 2.43, d = 0.59, e = 0.14;
    const out = dst || new Float32Array(rgba.length);
    for (let i = 0; i < rgba.length; i += 4) {
        const r = rgba[i], g = rgba[i + 1], bl = rgba[i + 2];
        out[i]     = clamp01((r * (a * r + b))  / (r * (c * r + d) + e));
        out[i + 1] = clamp01((g * (a * g + b))  / (g * (c * g + d) + e));
        out[i + 2] = clamp01((bl * (a * bl + b))/ (bl * (c * bl + d) + e));
        out[i + 3] = rgba[i + 3];
    }
    return out;
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

/**
 * Filmic with chrominance preservation — Phase 3.B-color (P3).
 *
 * Per-channel filmic shifts hue on saturated colors because each RGB
 * primary moves independently along the curve. darktable's filmic v7
 * fix (Sobotka et al. 2023, public maths): apply the curve to a single
 * NORM, then scale all three channels by the same `f(N)/N` ratio.
 * Pixel hue is preserved exactly (RGB ratio invariant). The slider
 * `mix ∈ [0,1]` blends between per-channel (0) and norm-preserved (1).
 *
 * Norm choice: `max(R,G,B)` — best compromise per the darktable v7
 * picker analysis. Euclidean over-desaturates highlights; luminance
 * fails on saturated blues; max-RGB is closest to LR/Capture One
 * defaults. Edge case: norm == 0 (pure black) → pass through.
 *
 * Why we needed it (RAW pain point): Canon-RAW reds shift toward
 * orange after filmic because R's per-channel curve compresses
 * faster than G's. This module fixes that.
 *
 * @param {Float32Array} rgba   ACEScg-linear RGBA Float32
 * @param {number}       mix    0 = per-channel filmic (legacy), 1 = norm-preserved
 * @param {Float32Array} [dst]
 */
export function filmicACESPreserveHue(rgba, mix, dst) {
    const a = 2.51, b = 0.03, c = 2.43, d = 0.59, e = 0.14;
    const out = dst || new Float32Array(rgba.length);
    const m = mix < 0 ? 0 : mix > 1 ? 1 : mix;
    const onePix = (v) => clamp01((v * (a * v + b)) / (v * (c * v + d) + e));
    const oneMinusM = 1 - m;
    for (let i = 0; i < rgba.length; i += 4) {
        const r = rgba[i], g = rgba[i + 1], bl = rgba[i + 2];
        /* Per-channel branch (legacy filmic). */
        const pr = onePix(r), pg = onePix(g), pb = onePix(bl);
        if (m === 0) {
            out[i] = pr; out[i + 1] = pg; out[i + 2] = pb;
        } else {
            /* Norm = max(R,G,B). Apply curve to norm, scale channels by f(N)/N. */
            const N = r > g ? (r > bl ? r : bl) : (g > bl ? g : bl);
            if (N <= 1e-7) {
                /* Pure black — no scaling needed; per-channel result is also zero. */
                out[i] = pr; out[i + 1] = pg; out[i + 2] = pb;
            } else {
                const Nout = onePix(N);
                const k = Nout / N;
                /* Norm-preserved channels (hue invariant). */
                const nr = clamp01(r * k);
                const ng = clamp01(g * k);
                const nb = clamp01(bl * k);
                /* Mix between per-channel (saturated, hue-shifted) and
                 * norm-preserved (hue-correct, may look flat). Clamp
                 * the mix too so out-of-gamut inputs can't escape
                 * (Codex Wave-1 STOP — also fixed in sigmoid below). */
                out[i]     = clamp01(oneMinusM * pr + m * nr);
                out[i + 1] = clamp01(oneMinusM * pg + m * ng);
                out[i + 2] = clamp01(oneMinusM * pb + m * nb);
            }
        }
        out[i + 3] = rgba[i + 3];
    }
    return out;
}

/**
 * Sigmoid with chrominance preservation — same shape, same slider semantics.
 * Math from darktable 4.4+'s rgb-ratio mode (per-channel mix where the
 * `rgb-ratio` toggle scales channels by f(N)/N for the norm-preserved branch).
 */
export function sigmoidACESPreserveHue(rgba, mix, dst) {
    const out = dst || new Float32Array(rgba.length);
    const skew     = SIGMOID_SKEW;
    const offset   = SIGMOID_OFFSET;
    const contrast = SIGMOID_CONTRAST;
    const white    = SIGMOID_WHITE;
    const evFloor  = SIGMOID_EV_FLOOR;
    const log2Grey = SIGMOID_LOG2_GREY;
    const m = mix < 0 ? 0 : mix > 1 ? 1 : mix;
    const oneMinusM = 1 - m;
    const onePix = (v) => {
        const ev = Math.log2(v > evFloor ? v : evFloor) - log2Grey;
        const s = 1 / (1 + Math.exp(-skew * ev - offset));
        return white * Math.pow(s, contrast);
    };
    for (let i = 0; i < rgba.length; i += 4) {
        const r = rgba[i], g = rgba[i + 1], bl = rgba[i + 2];
        const pr = onePix(r), pg = onePix(g), pb = onePix(bl);
        if (m === 0) {
            out[i] = pr; out[i + 1] = pg; out[i + 2] = pb;
        } else {
            const N = r > g ? (r > bl ? r : bl) : (g > bl ? g : bl);
            if (N <= evFloor) {
                out[i] = pr; out[i + 1] = pg; out[i + 2] = pb;
            } else {
                const Nout = onePix(N);
                const k = Nout / N;
                /* Clamp the ratio branch — without clamp01, an out-of-
                 * gamut input like [-0.5, 1, 0] passes through to
                 * negative output. Codex Wave-1 STOP. Mirrors filmic
                 * branch above. */
                const nr = clamp01(r * k);
                const ng = clamp01(g * k);
                const nb = clamp01(bl * k);
                /* Re-clamp the mix output too — partial mix between a
                 * clamped-positive `pr` and a clamped-positive `nr`
                 * stays in [0, 1] mathematically, but defensive in
                 * case future norm choices change. */
                out[i]     = clamp01(oneMinusM * pr + m * nr);
                out[i + 1] = clamp01(oneMinusM * pg + m * ng);
                out[i + 2] = clamp01(oneMinusM * pb + m * nb);
            }
        }
        out[i + 3] = rgba[i + 3];
    }
    return out;
}

/**
 * Sigmoid view-transform — modified generalized log-logistic curve
 * (darktable 4.4+, public mathematics — NOT a polynomial).
 *
 *   ev(x)     = log2(max(x, EV_FLOOR)) - log2(grey_point)
 *   shaped(x) = 1 / (1 + exp(-skew · ev(x) - offset))
 *   y(x)      = white · shaped(x) ^ contrast
 *
 * `offset` is the closed-form term that makes `grey_point` map to 0.5
 * after the `^contrast` step (Codex r1 STOP-1 caught the original draft
 * which lacked this term):
 *
 *   offset = -log( 1 / (0.5 ^ (1/contrast)) - 1 )    // logit(0.5^(1/contrast))
 *
 * For the BLRRD defaults (contrast=1.5, skew=1.0, grey=0.18, white=1.0):
 *
 *   offset ≈ 0.5318  →  sigmoid(0.18) = 0.5 (regression-guarded by
 *                       scripts/test-sigmoid-view-transform.mjs).
 *
 * Cost: ~1 log + 1 exp + 1 pow per pixel — same order as filmicACES.
 * Per-channel (matches `filmicACES` shape). Preserve-hue is out of scope.
 *
 * Spec: docs/superpowers/specs/2026-05-07-sigmoid-view-transform.md.
 *
 * @param {Float32Array} rgba   ACEScg-linear RGBA Float32
 * @param {Float32Array} [dst]
 * @returns {Float32Array}      [0..white] display-linear RGBA (still needs ODT encode)
 */

const SIGMOID_GREY     = 0.18;
const SIGMOID_WHITE    = 1.0;
const SIGMOID_SKEW     = 1.0;
const SIGMOID_CONTRAST = 1.5;
const SIGMOID_EV_FLOOR = 1e-6;

/* Closed-form offset so that `y(grey_point) = 0.5 · white`.
 * shaped(grey)^contrast = 0.5  →  shaped(grey) = 0.5 ^ (1/contrast)
 * ev(grey) = 0  →  1 / (1 + exp(-offset)) = 0.5^(1/contrast)
 * offset = -log( 1 / (0.5^(1/contrast)) - 1 ) = logit(0.5^(1/contrast)).
 * Computed once at module init from constants — no per-pixel cost. */
const SIGMOID_OFFSET = -Math.log(1 / Math.pow(0.5, 1 / SIGMOID_CONTRAST) - 1);

const SIGMOID_LOG2_GREY = Math.log2(SIGMOID_GREY);
const SIGMOID_LOG2_INV  = 1 / Math.log(2); /* multiplier converts log → log2 */

export function sigmoidACES(rgba, dst) {
    const out = dst || new Float32Array(rgba.length);
    const skew     = SIGMOID_SKEW;
    const offset   = SIGMOID_OFFSET;
    const contrast = SIGMOID_CONTRAST;
    const white    = SIGMOID_WHITE;
    const evFloor  = SIGMOID_EV_FLOOR;
    const log2Grey = SIGMOID_LOG2_GREY;

    for (let i = 0; i < rgba.length; i += 4) {
        const r = rgba[i], g = rgba[i + 1], bl = rgba[i + 2];

        /* Per-channel sigmoid. Math.log(x) * SIGMOID_LOG2_INV is faster
         * than Math.log2 in some JIT paths; spec'd as log2 for clarity. */
        const evR = Math.log2(r > evFloor ? r : evFloor) - log2Grey;
        const evG = Math.log2(g > evFloor ? g : evFloor) - log2Grey;
        const evB = Math.log2(bl > evFloor ? bl : evFloor) - log2Grey;

        const sR = 1 / (1 + Math.exp(-skew * evR - offset));
        const sG = 1 / (1 + Math.exp(-skew * evG - offset));
        const sB = 1 / (1 + Math.exp(-skew * evB - offset));

        out[i]     = white * Math.pow(sR, contrast);
        out[i + 1] = white * Math.pow(sG, contrast);
        out[i + 2] = white * Math.pow(sB, contrast);
        out[i + 3] = rgba[i + 3];
    }
    /* Suppress unused-warning on the alt log2 path; kept for future profiling. */
    void SIGMOID_LOG2_INV;
    return out;
}
